import { graphql, useMutation, useQuery } from "@opensea/graphql"
import { TimelineSkeleton, withSuspense } from "@opensea/ui-kit"
import { useCallback } from "react"
import { ActionTimeline } from "../../../../../components/ActionTimeline"
import { ActionTimelineFragment } from "../../../../../components/ActionTimeline/ActionTimeline.fragment"
import { useAddress } from "../../../../../polychain/hooks"
import type { SchedulableResponse } from "../../../../../polychain/hooks/useScheduler"
import { useTrackError } from "../../../../../providers/TrackErrorProvider"
import type { FlowType } from "../../../../flow-state"
import { readOrderCreationError } from "../../../../utils/readOrderCreationError"
import { readOrderCreationErrorFragment } from "../../../../utils/readOrderCreationError.fragment"
import { seaportOrder } from "../../../../utils/seaportOrder"
import { seaportOrderFragment } from "../../../../utils/seaportOrder.fragment"
import type { Listing } from "../../types"

const ListingFlowTimelineQuery = graphql(
  `
    query ListingFlowTimelineQuery(
      $listings: [ItemListingInput!]!
      $address: Address!
      $useCreatorFee: Boolean!
      $taker: Address
    ) {
      createListingsActions(
        listings: $listings
        address: $address
        useCreatorFee: $useCreatorFee
        taker: $taker
      ) {
        actions {
          __typename
          ... on CreateListingsAction {
            items {
              chain {
                identifier
              }
            }
            orders {
              ...seaportOrder
            }
          }
          ...ActionTimeline
        }
      }
    }
  `,
  [ActionTimelineFragment, seaportOrderFragment],
)

const ListingsFlowTimelineMutation = graphql(
  `
    mutation ListingsFlowTimelineMutation(
      $chain: ChainIdentifier!
      $orders: [SeaportOrderComponentsInput!]!
      $signature: String!
    ) {
      createListingsV2(chain: $chain, orders: $orders, signature: $signature) {
        ...readOrderCreationError
      }
    }
  `,
  [readOrderCreationErrorFragment],
)

export const ListingFlowTimeline = withSuspense(
  ({
    listings,
    useCreatorFee,
    onSuccess,
    onError,
    onBack,
  }: {
    listings: Listing[]
    useCreatorFee: boolean
    onSuccess?: () => void
    onError?: (error: Error) => void
    onBack?: () => void
  }) => {
    const wallet = useAddress()

    const [{ data }] = useQuery({
      query: ListingFlowTimelineQuery,
      variables: {
        address: wallet as NonNullable<string>,
        useCreatorFee,
        listings: listings.map(listing => ({
          item: {
            tokenId: listing.item.tokenId,
            contractAddress: listing.item.contractAddress,
            chain: listing.item.chain.identifier,
          },
          quantity: listing.quantity,
          pricePerItem: {
            unit: listing.pricePerItem.unit.toString(),
            contractAddress: listing.pricePerItem.contractAddress,
          },
          startTime: listing.startTime.toISOString(),
          endTime: listing.endTime.toISOString(),
        })),
        taker: listings[0]?.reservedBuyer,
      },
      pause: !wallet,
    })

    const trackError = useTrackError()

    const [_, mutate] = useMutation(ListingsFlowTimelineMutation)

    const onDone = useCallback(
      async (response: SchedulableResponse) => {
        if (!data) {
          return
        }

        if (response.action !== "SIGNATURE") {
          throw new Error("No signature response found from scheduled actions")
        }

        const actions = data.createListingsActions.actions
        const chain = actions.flatMap(a =>
          a.__typename === "CreateListingsAction" ? [a] : [],
        )[0].items[0].chain

        const orders = actions
          .flatMap(a =>
            a.__typename === "CreateListingsAction" ? a.orders : [],
          )
          .map(o => seaportOrder(o))

        const mutation = await mutate({
          orders,
          chain: chain.identifier,
          signature: response.signature,
        })

        const dataError = readOrderCreationError(
          mutation.data?.createListingsV2,
        )

        if (mutation.error) {
          trackError(mutation.error, {
            extra: { listings },
            tags: { flow: "listing" satisfies FlowType, address: wallet || "" },
          })
          onError?.(new Error(mutation.error.message))
        } else if (dataError) {
          onError?.(new Error(dataError.message))
        } else {
          onSuccess?.()
        }
      },
      [data, listings, mutate, onError, onSuccess, trackError, wallet],
    )

    if (!data) {
      return <TimelineSkeleton />
    }

    const actions = data.createListingsActions.actions

    return (
      <ActionTimeline
        actions={actions}
        onAllActionsCompleted={onDone}
        onBack={onBack}
      />
    )
  },
  <TimelineSkeleton />,
)
